import React, { useState, useEffect } from 'react';
import { FormControl, FormHelperText } from '@material-ui/core';

import { useTypedTranslation } from '@app/hooks';
import { PasslyCheckbox, PasslySelect, PasslyTextField } from '@app/components';
import { syncIntervals, active, disabled, getAgentDisplayFromType, doesPolicyRequireMFA, doesPolicySetPasswordFalse, interpolateTranslation } from '@app/helpers';
import { IAgentConfigDisplay, IPostAgentConfig, IPolicyAuth, AgentTypeEnum } from '@app/models';
import { ImageUpload } from '@app/pages/directoryManager/imageUpload/imageUpload.component';
import { applicationsService } from '@app/services';

interface IProps {
    agentTypeArg: string;
    configDataChanged: Function;
    agentConfigArg: IPostAgentConfig;
    authPolicies: IPolicyAuth[];
}

const AddEditAgentConfig: React.SFC<IProps> = ({ agentTypeArg, configDataChanged, agentConfigArg, authPolicies }) => {
    const { translation } = useTypedTranslation();

    const [
        {
            SyncIntervalMinutes,
            WLA2FATimeoutSeconds,
            IconUrl,
            AgentName,
            OrganizationId,
            ClientId,
            ProvisioningState,
            AgentType,
            DownloadLocation,
            PolicyId,
            RequiresConsent,
            Id,
            RedirectUri
        },
        setState
    ] = useState(agentConfigArg);
    const [agentConfigDisplay] = useState<IAgentConfigDisplay>(getAgentDisplayFromType(agentTypeArg));
    const [availablePolicies] = useState<IPolicyAuth[]>(getAvailableAuthPolicies());

    useEffect(() => {
        if (PolicyId === '' && availablePolicies.length !== 0) {
            setState((prevState) => ({ ...prevState, PolicyId: availablePolicies[0].Id }));
        }
    }, [availablePolicies]);

    function createAgentConfig(): IPostAgentConfig {
        return {
            Version: '1.0',
            '@odata.type': 'Kaseya.AuthAnvil.Models.AgentProfile',
            AgentName: AgentName,
            SyncIntervalMinutes: SyncIntervalMinutes,
            WLA2FATimeoutSeconds: WLA2FATimeoutSeconds,
            IconUrl: IconUrl,
            OrganizationId: OrganizationId,
            ClientId: ClientId,
            ProvisioningState: ProvisioningState,
            AgentType: AgentType,
            DownloadLocation: DownloadLocation,
            RedirectUri: RedirectUri,
            RequiresConsent: RequiresConsent,
            PolicyId: PolicyId,
            Id: Id
        } as IPostAgentConfig;
    }

    function getAvailableAuthPolicies(): IPolicyAuth[] {
        if (agentTypeArg === AgentTypeEnum.windowsLogon) {
            return authPolicies.filter((authPolicy) => {
                return doesPolicyRequireMFA(authPolicy) && doesPolicySetPasswordFalse(authPolicy);
            });
        } else {
            return authPolicies;
        }
    }

    function updateParentConfig(name: any, value: any) {
        let updatedConfig = createAgentConfig();
        updatedConfig[name] = value;

        configDataChanged(updatedConfig);
    }

    const handleOnImageChange = (newImageUrl: string) => {
        setState((prevState) => ({ ...prevState, IconUrl: newImageUrl }));
        updateParentConfig('IconUrl', newImageUrl);
    };

    const onFormChange = (e: any) => {
        const { name, value } = e.target;

        setState((prevState) => ({ ...prevState, [name]: value }));

        updateParentConfig(name, value);
    };

    const onCheckboxChange = (checked: boolean, e: any) => {
        const { name } = e.target;

        setState((prevState) => ({ ...prevState, [name]: checked }));

        updateParentConfig(name, checked);
    };

    const onProvisioningChange = (checked: boolean, e: any) => {
        const { name } = e.target;

        let valueToSet = checked ? active : disabled;
        setState((prevState) => ({ ...prevState, [name]: valueToSet }));

        updateParentConfig(name, valueToSet);
    };

    const args = agentConfigDisplay.WLA2FATimeoutSeconds
        ? [
              `<li><b>Agent Timeout:</b> This indicates how long the agent should wait before closing the authentication connection during an authentication request.</li>`
          ]
        : [''];
    const agentConfigHelperText = interpolateTranslation(translation('AGENT_CONFIG_HELPER_HEADER'), args);

    return (
        <div className='rowcontainer'>
            <div className='configpanel-left configpanel-left--add-edit-agent-config'>
                <form className='modal__form' method='post'>
                    <div className='modal__form--checkbox'>
                        <PasslyCheckbox
                            name='ProvisioningState'
                            checked={ProvisioningState === active}
                            onChange={onProvisioningChange}
                            label={translation('AGENT_ENABLED')}
                        />
                    </div>
                    <FormControl>
                        <PasslyTextField
                            fullWidth
                            name='AgentName'
                            label={'AGENT_NAME_LABEL'}
                            value={AgentName}
                            onChange={onFormChange}
                            id='agentName-input'
                            inputProps={{ maxLength: 250 }}
                            error={AgentName === ''}
                            required
                            autoFocus />
                    </FormControl>

                    {agentConfigDisplay.RedirectURI &&
                        (<FormControl>
                            <PasslyTextField
                                fullWidth
                                name='RedirectUri'
                                label={'AGENT_REDIRECT_URI'}
                                value={RedirectUri}
                                onChange={onFormChange}
                                id='redirectUri-input'
                                error={RedirectUri === ''}
                                required
                            />
                        </FormControl>
                    )}

                    {agentConfigDisplay.RequiresConsent && (
                        <div className='modal__form--checkbox'>
                            <PasslyCheckbox
                                name='RequiresConsent'
                                checked={RequiresConsent || false}
                                onChange={onCheckboxChange}
                                label={translation('AGENT_REQUIRES_CONSENT')}
                            />
                        </div>
                    )}

                    <ImageUpload
                        width={100}
                        height={100}
                        currentImageUrl={IconUrl}
                        onFileUpload={applicationsService.uploadImage}
                        onImageChange={handleOnImageChange}
                    />

                    {agentConfigDisplay.SyncIntervalMinutes && (
                        <FormControl>
                            <PasslySelect
                                name='SyncIntervalMinutes'
                                value={SyncIntervalMinutes}
                                onChange={onFormChange}
                                id='syncInterval-input'
                                label={'SYNC_INTERVAL'}
                                options={syncIntervals}
                            />
                        </FormControl>
                    )}
                    {agentConfigDisplay.WLA2FATimeoutSeconds &&
                        (<FormControl>
                            <PasslyTextField
                                fullWidth
                                name='WLA2FATimeoutSeconds'
                                label={'APP_WLA_2FA_TIMEOUT'}
                                value={WLA2FATimeoutSeconds}
                                onChange={onFormChange}
                                id='wla2FATimeoutSeconds-input'
                                inputProps={{ maxLength: 250 }}
                                error={!WLA2FATimeoutSeconds}
                                required
                                autoFocus
                            />
                        </FormControl>
                    )}

                    {agentConfigDisplay.PolicyId && (
                        <FormControl error={availablePolicies.length === 0}>
                            {agentTypeArg === AgentTypeEnum.windowsLogon && availablePolicies.length === 0 && (
                                <FormHelperText className='configpanel-left--add-edit-agent-config--error'>
                                    {translation('WINDOWS_LOGON_REQUIRE_MFA_MESSAGE')}
                                </FormHelperText>
                            )}
                            <PasslySelect
                                disabled={availablePolicies.length === 0}
                                name='PolicyId'
                                value={PolicyId}
                                onChange={onFormChange}
                                id='authPolicyId-input'
                                label={'APP_AUTH_POLICY'}
                                options={availablePolicies}
                                optionLabel={'DisplayName'}
                                optionValue={'Id'}
                                appendTo={document.body}
                            />
                        </FormControl>
                    )}
                </form>
            </div>
            <div className='configpanel-right configpanel-right--add-edit-agent-config'>
                {agentTypeArg === AgentTypeEnum.openidConnect ? (
                    <div dangerouslySetInnerHTML={{ __html: translation('AGENT_OPENID_CONNECT_HELPER_HEADER') }} />
                ) : (
                    <div dangerouslySetInnerHTML={{ __html: agentConfigHelperText }} />
                )}
            </div>
        </div>
    );
};

export default AddEditAgentConfig;
